import { ReducerCreators } from '@reduxjs/toolkit';
import { serializeError } from '@/store/utils/serialize-error';

import { AnnouncementsState } from './types';
import {
  DeleteAnnouncement,
  GetScheduledAnnouncements,
  IUpdateAnnouncementParams,
  SaveAnnouncement_V2,
  UpdateAnnouncement_V2,
} from '@/connection/announcements/announcements';
import { IAnnouncement } from '@/types/announcement.types';
import toastTrigger from '@ui/toast/toast-trigger';

export const reducers = (create: ReducerCreators<AnnouncementsState>) => ({
  fetchAnnouncements: create.asyncThunk(
    async ({ projectUuid }: { projectUuid: string }) => {
      const announcements = await GetScheduledAnnouncements(projectUuid);
      return { projectUuid, announcements };
    },
    {
      fulfilled: (state, action) => {
        if (action.payload.projectUuid) {
          state.scheduledAnnouncements[action.payload.projectUuid] = action.payload.announcements || [];
        }
        state.loadingAnnouncements = false;
      },
      pending: (state) => {
        state.loadingAnnouncements = true;
      },
      rejected: (state, action) => {
        state.loadingAnnouncements = false;
        console.error(action.error);
      },
    }
  ),
  saveAnnouncementV2: create.asyncThunk(
    async (
      {
        content,
        defaultLanguage,
        channel,
        projectUuid,
      }: { content: IAnnouncement; defaultLanguage: string; channel: number; projectUuid: string },
      { rejectWithValue }
    ) => {
      try {
        const announcement = await SaveAnnouncement_V2({
          channel,
          content,
          defaultLanguage,
        });

        return { projectUuid, announcement };
      } catch (error) {
        console.error(error);
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.scheduledAnnouncements[action.payload.projectUuid]) {
          state.scheduledAnnouncements[action.payload.projectUuid].push(action.payload.announcement);
        }
        state.savingAnnouncement = false;
      },
      pending: (state) => {
        state.savingAnnouncement = true;
      },
      rejected: (state, action) => {
        state.savingAnnouncement = false;
        console.error(action.error);
      },
    }
  ),
  updateAnnouncementV2: create.asyncThunk(
    async (
      {
        announcement,
        defaultLanguage,
        channel,
        previouslyScheduledSwitchedToImmediate,
        projectUuid,
      }: IUpdateAnnouncementParams & { projectUuid: string },
      { rejectWithValue }
    ) => {
      try {
        const updatedAnnouncement = await UpdateAnnouncement_V2({
          announcement,
          defaultLanguage,
          channel,
          previouslyScheduledSwitchedToImmediate,
        });

        return { projectUuid, announcement: updatedAnnouncement };
      } catch (error) {
        console.error(error);
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.scheduledAnnouncements[action.payload.projectUuid]) {
          state.scheduledAnnouncements[action.payload.projectUuid] = state.scheduledAnnouncements[
            action.payload.projectUuid
          ].map((announcement) => {
            if (announcement.uuid === action.payload.announcement.uuid) {
              return action.payload.announcement;
            }
            return announcement;
          });
        }
        state.savingAnnouncement = false;
      },
      pending: (state) => {
        state.savingAnnouncement = true;
      },
      rejected: (state, action) => {
        state.savingAnnouncement = false;
        console.error(action.error);
      },
    }
  ),
  deleteAnnouncement: create.asyncThunk(
    async (
      { announcement, projectUuid }: { announcement: IAnnouncement; projectUuid: string },
      { rejectWithValue }
    ) => {
      try {
        await DeleteAnnouncement(announcement);

        return { announcement, projectUuid };
      } catch (error) {
        console.error(error);
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.scheduledAnnouncements[action.payload.projectUuid]) {
          state.scheduledAnnouncements[action.payload.projectUuid] = state.scheduledAnnouncements[
            action.payload.projectUuid
          ].filter((announcement) => announcement.uuid !== action.payload.announcement.uuid);
        }
        state.savingAnnouncement = false;
      },
      pending: (state) => {
        state.savingAnnouncement = true;
      },
      rejected: (state, action) => {
        state.savingAnnouncement = false;
        console.error(action.error);
      },
    }
  ),
  cancelAnnouncementV2: create.asyncThunk(
    async (
      {
        announcement,
        projectUuid,
        channel,
        defaultLanguage,
      }: { announcement: IAnnouncement; projectUuid: string; channel: number; defaultLanguage: string },
      { rejectWithValue }
    ) => {
      try {
        const updatedAnnouncement = await UpdateAnnouncement_V2({
          defaultLanguage,
          announcement,
          cancelAnnouncement: true,
          channel,
        });

        return { announcement: updatedAnnouncement, projectUuid };
      } catch (error) {
        console.error(error);
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.scheduledAnnouncements[action.payload.projectUuid]) {
          state.scheduledAnnouncements[action.payload.projectUuid] = state.scheduledAnnouncements[
            action.payload.projectUuid
          ].map((announcement) => {
            if (announcement.uuid === action.payload.announcement.uuid) {
              return action.payload.announcement;
            }
            return announcement;
          });
        }
        state.savingAnnouncement = false;
      },
      pending: (state) => {
        state.savingAnnouncement = true;
      },
      rejected: (state, action) => {
        state.savingAnnouncement = false;
        console.error(action.error);
        toastTrigger(
          '<div class="S2">Error cancelling announcement.</div><div class="S2">Please refresh and try again</div>',
          { type: 'error' }
        );
      },
    }
  ),
});
